import { SIGN_ORIGIN } from "../../../../common/enum/common";
import type { Page } from "../../../../common/types/base/BaseEntity";
import type { PurchaseGoodsReceiptForm } from "../../../../common/types/purchaseGoodsReceipt";
import { BaseService } from "./baseService";
import httpRequest from "./index";
import { confirmable } from "@/api/legacy";
import { analysisState } from "@/api/legacy";
import type { PurchaseGoodsReceipt, PurchaseGoodsReceiptVo } from "@zhihaoscm/entity";

export class PurchaseGoodsReceiptService extends BaseService<
    PurchaseGoodsReceipt,
    PurchaseGoodsReceiptVo,
    any,
    PurchaseGoodsReceiptForm
> {
    /**
     * 分页查询
     * @param params
     */
    // @ts-ignore
    async paging(params: any): Promise<Page<PurchaseGoodsReceiptVo>> {
        const { data } = await this.service.get(`${this.path}/custom-paging`, { params });
        if (data.list?.length) {
            data.list.forEach((item: PurchaseGoodsReceiptVo) => {
                (item.purchaseGoodsReceipt as any).complexState = item.purchaseGoodsReceipt.state;
                item.purchaseGoodsReceipt.state = analysisState.getNormalState(item.purchaseGoodsReceipt.state);
                item.relatedPurchaseGoodsReceiptList?.forEach(item => {
                    item.state = analysisState.getNormalState(item.state);
                });
            });
        }
        return data;
    }

    /**
     * 按 ID 获取对象
     * @param id
     */
    // @ts-ignore
    async getInfo(id: number | string): Promise<PurchaseGoodsReceiptVo> {
        const { data }: { data: PurchaseGoodsReceiptVo } = await this.service.get(`${this.path}/vo/${id}`);
        if (data.purchaseGoodsReceipt) {
            (data as any).purchaseGoodsReceipt.complexState = data.purchaseGoodsReceipt.state;
            data.purchaseGoodsReceipt.state = analysisState.getNormalState(data.purchaseGoodsReceipt.state);
        }
        if (data.relatedPurchaseGoodsReceiptList) {
            data.relatedPurchaseGoodsReceiptList.forEach(item => {
                item.state = analysisState.getNormalState(item.state);
            });
        }
        return data;
    }

    /**
     * 根据项目 id 和类型查询签收列表
     * @param projectId
     * @param type
     * @param params
     */
    async getListByProjectId(projectId: number, type: number, params: any): Promise<Page<PurchaseGoodsReceipt>> {
        const { data } = await this.service.get(`${this.path}/paging/project-id/${projectId}/type/${type}`, { params });
        if (data?.list.length) {
            data.list.forEach((item: PurchaseGoodsReceipt) => {
                (item as any).complexState = item.state;
                item.state = analysisState.getNormalState(item.state);
            });
        }
        return data;
    }

    /** 发起签署 */
    async startSign(id: string): Promise<any> {
        const { data } = await this.service.put(`${this.path}/initiateSigning/${id}?origin=${SIGN_ORIGIN.PC.code}`, {});
        return data;
    }

    /** 签署 */
    async sign(id: string): Promise<{ ticket: string; errors: unknown[]; status: number }> {
        const { data } = await this.service.put(`${this.path}/signing/${id}?origin=${SIGN_ORIGIN.PC.code}`, {});
        return data;
    }

    /**
     * 下拉选框列表数据
     */
    // @ts-ignore
    public async selector(params?: { projectId: string }): Promise<PurchaseGoodsReceiptVo[]> {
        const { data } = await this.service.get(`${this.path}/supplier-selector`, { params });
        if (data?.length) {
            (data as PurchaseGoodsReceiptVo[]).forEach(item => {
                (item as any).purchaseGoodsReceipt.complexState = item.purchaseGoodsReceipt.state;
                item.purchaseGoodsReceipt.state = analysisState.getNormalState(item.purchaseGoodsReceipt.state);
            });
        }
        return data;
    }

    /** 驳回 */
    public async reject(params?: { id: string; reason: string }) {
        await this.service.put(`${this.path}/reject`, params);
    }

    /**
     * 根据项目 id 查询未关联对账签收单
     * @param projectId
     * @param receiverCustomerId
     */
    async findByProjectId(projectId: number, receiverCustomerId: number): Promise<PurchaseGoodsReceiptVo[]> {
        const { data } = await this.service.get(`${this.path}/findByProjectId/${projectId}`, {
            params: { receiverCustomerId }
        });
        if (data?.length) {
            data.forEach((item: PurchaseGoodsReceiptVo) => {
                (item.purchaseGoodsReceipt as any).complexState = item.purchaseGoodsReceipt.state;
                item.purchaseGoodsReceipt.state = analysisState.getNormalState(item.purchaseGoodsReceipt.state);
            });
        }
        return data;
    }

    /**
     * 创建实例
     * @param form
     */
    // @ts-ignore
    public async create(form: PurchaseGoodsReceiptForm): Promise<any> {
        for (const key in form) {
            const value = form[key as keyof PurchaseGoodsReceiptForm];
            if (value === undefined || value === null || value === "") {
                delete form[key as keyof PurchaseGoodsReceiptForm];
            }
        }
        const { data } = await this.service.post(this.path, form, {
            loading: true
        } as any);
        return data;
    }

    /**
     * 更新实例
     * @param id
     * @param form
     */
    // @ts-ignore
    public async update(id: number | string, form: PurchaseGoodsReceiptForm) {
        const { data } = await this.service.put(`${this.path}/${id}`, form, {
            loading: true
        } as any);
        return data;
    }

    /**
     * 根据关联签收单id和状态查询签收vo列表
     * @param params
     */
    async getAssociateReceiptsById(params: {
        relatedReceiptId: string;
        state?: number;
    }): Promise<Array<PurchaseGoodsReceiptVo>> {
        const { data } = await this.service.get(`${this.path}/vo/relatedReceiptId`, { params });
        if (data?.length) {
            data.forEach((item: PurchaseGoodsReceipt) => {
                item.state = analysisState.getNormalState(item.state);
            });
        }
        return data;
    }

    /**
     * 作废提货
     */
    async startInvalid(id: number): Promise<{
        pageUrl: string;
        ticket?: string;
        errors: unknown[];
        status: number;
    }> {
        const { data } = await this.service.put(`${this.path}/invalid/${id}?origin=${SIGN_ORIGIN.PC.code}`);
        return data;
    }
}

export const purchaseGoodsReceiptService = new PurchaseGoodsReceiptService("/purchase/goods/receipt", httpRequest);
